import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, signal, inject } from '@angular/core';
import { SubsidiariesService } from 'apps/console/src/app/subsidiaries-module/services/subsidiaries.service';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, debounceTime, takeUntil, mergeMap, filter, tap, share } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActionType, AppConstants, StoreLoader, ValidationService, mapToFullNameString } from '@aston/foundation';
import { ISubsidiaryFormUserDelegations, ISubsidiaryFormUserInactivity, MiddleUsersCreateFormModalComponent, MiddleUsersDelegationFormModalComponent, MiddleUsersDelegationViewModalComponent, MiddleUsersInactivityFormModalComponent, UserStatus } from '@aston/middle-users-admin';

import { AppStoreActions } from '../app-store';
import * as appStoreSelectors from '../app-store/selectors';
import { ConsoleDomainValidators } from '../../shared-module/enums/validators';
import { UserListFilterSpecService } from '../../subsidiaries-module/services';

import * as featureSelectors from './selectors';
import * as featureActions from './actions';

@Injectable({providedIn: 'root'})
export class SubsidiaryDetailStoreEffects {
	private actions$ = inject(Actions);
	private store = inject(Store);
	protected modalService = inject(NgbModal);
	protected validationService = inject(ValidationService);
	private subsidiariesService = inject(SubsidiariesService);
	private ulSpecService = inject(UserListFilterSpecService);


	currentSubsidiaryRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSubsidiaryRequest),
		switchMap(action => this.subsidiariesService.getSubsidiary(action.id).pipe(
			map(subsidiary => featureActions.LoadSubsidiarySuccess({ entity: subsidiary }),
				catchError(error => of(featureActions.LoadSubsidiaryFailure({ error })))
			))
		)));

	updateSubsidiaryRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSubsidiaryRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([action, subsidiaryId]) => this.subsidiariesService.updateSubsidiary(subsidiaryId, action.entity).pipe(
			switchMap(_ => [
				featureActions.UpdateSubsidiarySuccess({ entity: action.entity }),
				featureActions.LoadSubsidiaryRequest(subsidiaryId)
			]),
			catchError(error => of(featureActions.UpdateSubsidiaryFailure({ error })))
		))
	));

	loadUsersListRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadUsersListRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectUsersListSlice),
			this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		debounceTime(500),
		switchMap(([action, usersListSlice, subsidiaryId]) => {
			const { filters, paging } = usersListSlice;
			return this.subsidiariesService.getSubsidiaryUsers(subsidiaryId, { ...paging, filters }).pipe(
				map(list => featureActions.LoadUsersListSuccess({ list, correlationParams: action.correlationParams })),
				catchError(error => of(featureActions.LoadUsersListFailure({ error })))
			)
		})
	));

	UpdateUsersListSettings$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateUsersListSettings),
		map(_ => featureActions.LoadUsersListRequest())
	));

	UpdateTransientUserStatusRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateTransientUserStatusRequest),
		withLatestFrom(
			this.store.select(appStoreSelectors.selectUser),
			this.store.select(featureSelectors.selectUsersList),
			this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([action, _, __, subsidiaryId]) => this.subsidiariesService.updateUserStatus(subsidiaryId, action.entity).pipe(
			switchMap(_ => [
				featureActions.UpdateTransientUserStatusSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest(),
			]),
			catchError(error => of(featureActions.UpdateTransientUserStatusFailure({ error }), featureActions.LoadUsersListRequest()))
		))
	))

	UpdateTransientSubsidiaryRoleRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateTransientSubsidiaryRoleRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([action, subsidiaryId]) => this.subsidiariesService.updateSubsidiaryRole(subsidiaryId, action.entity).pipe(
			switchMap(_ => [
				featureActions.UpdateTransientSubsidiaryRoleSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest()
			]),
			catchError(error => of(featureActions.UpdateTransientSubsidiaryRoleFailure({ error }), featureActions.LoadUsersListRequest()))
		))
	))


	LoadAvailableRolesForCurrentSubsidiaryRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadAvailableRolesForCurrentSubsidiaryRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([_, subsidiaryId]) => this.subsidiariesService.getSubsidiaryRoles(subsidiaryId).pipe(
			map(list => featureActions.LoadAvailableRolesForCurrentSubsidiarySuccess({ entity: list })),
			catchError(error => of(featureActions.LoadAvailableRolesForCurrentSubsidiaryFailure({ error })))
		))
	))

	OnOpenSubsidiaryUserForm$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(featureActions.OpenSubsidiaryUserForm),
			withLatestFrom(
				this.store.select(featureSelectors.selectUsersList),
				this.store.select(featureSelectors.selectAvailableRolesForCurrentSubsidiarySlice)),
			map(([_, usersList, roles]) => {
				const modalRef = this.modalService.open(MiddleUsersCreateFormModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
				const modal = (modalRef.componentInstance as MiddleUsersCreateFormModalComponent);
				const tu$ = takeUntil(modal.destroySubscriptions$);

				modal.roles = signal(roles.entity);
				modal.usersList = usersList.items;
				modal.validationHelperGetter = this.validationService.getHelper(ConsoleDomainValidators.CreateRegistrationUserSubsidiaryValidator)
				modal.displayEmailAlreadyExistInGuestListError = _ => this.store.dispatch(featureActions.displayEmailAlreadyExistInGuestListError())
				modal.displayEmailAlreadyExistInUserListError = _ => this.store.dispatch(featureActions.displayEmailAlreadyExistInUserListError())

				modal.cancel.pipe(tu$).subscribe(_ => this.store.dispatch(featureActions.SetActiveModal({modal: null})))
				modal.submitForm.pipe(tu$).subscribe((entity: any) => {
					entity.forEach(element => {
						this.store.dispatch(featureActions.CreateSubsidiaryUserRequest(element))
						this.store.dispatch(featureActions.SetActiveModal({modal: null}))
					});
				});

				return featureActions.SetActiveModal({modal: modalRef});
			})
		);
	});

	OpenUserDelegationView$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(featureActions.OpenUserDelegationView),
			withLatestFrom(this.store.select(featureSelectors.selectSubsidiary)),
			map(([action]) => {
				const modalRef = this.modalService.open(MiddleUsersDelegationViewModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
				const modal = (modalRef.componentInstance as MiddleUsersDelegationViewModalComponent);
				modal.user = action.user;
				modal.roleLabelFn = t => `Roles.${t.toUpperCase()}`;
				modal.loader = new StoreLoader(
					this.store.select(featureSelectors.selectSubsidiaryUserDelegationsIsLoading),
					this.store.select(featureSelectors.selectSubsidiaryUserDelegationsError)
				);
				modal.delegations$ = this.store.select(featureSelectors.selectSubsidiaryUserDelegations);
				modal.loadDelegations = (userId: number) => this.store.dispatch(featureActions.LoadSubsidiaryUserDelegationsRequest(userId));

				return AppStoreActions.Noop();
			})
		);
	});

	OpenUserDelegationForm$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(featureActions.OpenUserDelegationForm),
			withLatestFrom(
				this.store.select(featureSelectors.selectCurrentSubsidiaryId),
				this.store.select(featureSelectors.selectSubsidiary),
			),
			map(([action, sid]) => {
				const modalRef = this.modalService.open(MiddleUsersDelegationFormModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
				const modal = (modalRef.componentInstance as MiddleUsersDelegationFormModalComponent);
				modal.user = action.user;
				modal.mode = action.mode;
				modal.loader = new StoreLoader(
					this.store.select(featureSelectors.selectSubsidiaryUserDelegationsFormIsLoading),
					this.store.select(featureSelectors.selectSubsidiaryUserDelegationsFormError)
				);
				modal.validationGetter = this.validationService.getHelper(ConsoleDomainValidators.CreateDelegationCommandValidator);
				modal.loadDelegations = (userId: number) => this.store.dispatch(featureActions.LoadSubsidiaryUserDelegationsFormRequest(userId));
				modal.delegations$ = this.store.select(featureSelectors.selectSubsidiaryUserDelegationsForm);
				modal.updateDelegations = (form: ISubsidiaryFormUserDelegations) => this.store.dispatch(featureActions.UpdateSubsidiaryUserDelegationsFormRequest(form));
				modal.users$ = this.subsidiariesService.getSubsidiaryUsers(sid, {
					pageSize: AppConstants.LIST_PAGE_SIZE_IGNORED,
					filters: this.ulSpecService.onlyActiveUsers()
				}).pipe(
					map(list => list.items
						// Bug 29872: Acc [Console-Entreprises-Utilisateurs] Delegation : Le user délégant est sélectionnable dans la fenêtre de délégation
						.filter(u => u.userId !== action.user.userId)
						// User Story 22909: [ACC][Délégation] Limiter la délégation depuis et vers un utilisateur Observateur
						.filter(u => (!u.isSameRoleOnlyDelegation && !action.user.isSameRoleOnlyDelegation) || u.subsidiaryRole === action.user.subsidiaryRole)
						.map(u => ({ label: mapToFullNameString(u), value: u.userId }))
					),
					share()
				);

				modal.cancelForm.pipe(
					takeUntil(modal.destroySubscriptions$)
				).subscribe(_ => this.store.dispatch(featureActions.SetActiveModal({modal: null})))

				return featureActions.SetActiveModal({ modal: modalRef });
			})
		);
	});

	OpenUserInactivityForm$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(featureActions.OpenUserInactivityForm),
			withLatestFrom(this.store.select(featureSelectors.selectSubsidiary)),
			map(([action]) => {
				const modalRef = this.modalService.open(MiddleUsersInactivityFormModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
				const modal = (modalRef.componentInstance as MiddleUsersInactivityFormModalComponent);
				modal.user = action.user;
				modal.mode = action.mode;
				modal.validationHelperGetter = this.validationService.getHelper(ConsoleDomainValidators.UpdateSubsidiaryUserInactivityCommandValidator);
				modal.updateUserInactivity = (form: ISubsidiaryFormUserInactivity) => this.store.dispatch(featureActions.UpdateSubsidiaryUserInactivityFormRequest(form));
				modal.loader = new StoreLoader(
					this.store.select(featureSelectors.selectSubsidiaryUserInactivityFormIsLoading),
					this.store.select(featureSelectors.selectSubsidiaryUserInactivityFormError)
				);

				modal.cancel.pipe(
					takeUntil(modal.destroySubscriptions$)
				).subscribe(_ => {
					this.modalService.dismissAll();
					this.store.dispatch(featureActions.SetActiveModal({modal: null}));
					this.store.dispatch(featureActions.LoadUsersListRequest());
				})

				return featureActions.SetActiveModal({ modal: modalRef });
			})
		);
	});

	UpdateSubsidiaryUserInactivityFormRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSubsidiaryUserInactivityFormRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([{entity}, subsidiaryId]) => this.subsidiariesService.updateUserInactivityOnSubsidiary(entity.forUserId, subsidiaryId, entity).pipe(
			switchMap(_ => {
				this.modalService.dismissAll();
				return [
					featureActions.UpdateSubsidiaryUserInactivityFormSuccess({entity}),
					featureActions.LoadUsersListRequest()
				]
			}),
			catchError(error => of(featureActions.UpdateSubsidiaryUserInactivityFormFailure({ error }))),
		))
	))

	CreateSubsidiaryUserRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CreateSubsidiaryUserRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		mergeMap(([action, subsidiaryId]) => this.subsidiariesService.postSubsidiaryUser(subsidiaryId, action.entity).pipe(
			switchMap(_ => [
				featureActions.CreateSubsidiaryUserSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest()
			]),
			catchError(error => of(featureActions.CreateSubsidiaryUserFailure({ error }), AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	));

	UpdateSubsidiaryRegistrationRequestRequestEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ResentUserInvitationRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		mergeMap(([action, subsidiaryId]) => this.subsidiariesService.resendInvitationToUser(subsidiaryId, action.entity.userId).pipe(
			switchMap(_ => [
				featureActions.ResentUserInvitationSuccess({ entity: action.entity }),
				featureActions.LoadUsersListRequest()
			]),
			catchError(error => of(featureActions.ResentUserInvitationtFailure({ error }), AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	));

	LoadSubsidiaryUserDelegationsRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSubsidiaryUserDelegationsRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([action, subsidiaryId]) => this.subsidiariesService.getUserDelegationsOnSubsidiary(subsidiaryId, action.id).pipe(
			map(entity =>featureActions.LoadSubsidiaryUserDelegationsSuccess({entity})),
			catchError(error => of(featureActions.LoadSubsidiaryUserDelegationsFailure({ error })))
		))
	));

	LoadSubsidiaryUserDelegationsFormRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadSubsidiaryUserDelegationsFormRequest),
		withLatestFrom(this.store.select(featureSelectors.selectCurrentSubsidiaryId)),
		switchMap(([action, subsidiaryId]) => {
			return this.subsidiariesService.getUserDelegationsFormOnSubsidiary(action.id, subsidiaryId).pipe(
				map(entity => featureActions.LoadSubsidiaryUserDelegationsFormSuccess({entity})),
				catchError(error => of(featureActions.LoadSubsidiaryUserDelegationsFormFailure({ error })))
			)
		})
	));

	CreateSubsidiaryUserDelegationsFormRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSubsidiaryUserDelegationsFormRequest),
		filter(action => action.entity.mode === ActionType.UPDATE),
		withLatestFrom(
			this.store.select(featureSelectors.selectCurrentSubsidiaryId),
			this.store.select(featureSelectors.selectActiveModal)),
		switchMap(([action, subsidiaryId, activeModal]) => {
			const entity = action.entity as ISubsidiaryFormUserDelegations;
			return this.subsidiariesService.updateUserDelegationsOnSubsidiary(entity.forUserId, subsidiaryId, entity).pipe(
				tap(_ => activeModal?.close()),
				switchMap(_ => [
					featureActions.UpdateSubsidiaryUserDelegationsFormSuccess({entity}),
					featureActions.SetActiveModal({modal: null}),
					featureActions.LoadUsersListRequest()
				]),
				catchError(error => of(featureActions.UpdateSubsidiaryUserDelegationsFormFailure({ error })))
			)
		})
	));

	UpdateSubsidiaryUserDelegationsFormRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSubsidiaryUserDelegationsFormRequest),
		filter(action => action.entity.mode === ActionType.CREATE),
		withLatestFrom(
			this.store.select(featureSelectors.selectCurrentSubsidiaryId),
			this.store.select(featureSelectors.selectActiveModal)),
		switchMap(([action, subsidiaryId, activeModal]) => {
			const entity = action.entity as ISubsidiaryFormUserDelegations;
			return this.subsidiariesService.createUserDelegationsOnSubsidiary(entity.forUserId, subsidiaryId, entity).pipe(
				tap(_ => activeModal?.close()),
				switchMap(_ => [
					featureActions.UpdateSubsidiaryUserDelegationsFormSuccess({entity}),
					featureActions.SetActiveModal({modal: null}),
					featureActions.LoadUsersListRequest()
				]),
				catchError(error => of(featureActions.UpdateSubsidiaryUserDelegationsFormFailure({ error })))
			)
		})
	));

	UpdateDelegatedUsersDelegationsFormRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSubsidiaryDelegatedUsersDelegationsRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectCurrentSubsidiaryId),
			this.store.select(featureSelectors.selectActiveModal)),
		switchMap(([action, subsidiaryId, activeModal]) => {
			return this.subsidiariesService.updateDelegatedUsersDelegationsOnSubsidiary(action.userId, subsidiaryId).pipe(
				tap(_ => activeModal?.close()),
				switchMap(_ => [
					AppStoreActions.InfoLog({ message: 'updateDelegatedUsersDelegationsOnSubsidiary success' })
				]),
				catchError(error => of(featureActions.UpdateSubsidiaryUserDelegationsFormFailure({ error })))
			)
		})
	));

	displayEmailAlreadyPresentInGuestListGuestErrorEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.displayEmailAlreadyExistInGuestListError),
		map(_ => AppStoreActions.ToastError('Errors.EmailAlreadyExistInGuestList'))
	));

	displayEmailAlreadyPresentErrorEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.displayEmailAlreadyExistInUserListError),
		map(_ => AppStoreActions.ToastError('Errors.EmailAlreadyExistInUserList'))
	));

	startUserStatusWorkflow$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.StartUserStatusWorkflow),
		withLatestFrom(
			this.store.select(appStoreSelectors.selectUser),
			this.store.select(featureSelectors.selectUsersList)),
		switchMap(([ event, selectedUser, usersList]) => {
			const result = [].concat(AppStoreActions.Noop());
			const { action, status } = event;

			const user = usersList.items.find(u => u.userId === action.value.userId);
			const currentUser = usersList.items.find(u => u.userId === selectedUser.id);

			const userStatusAction = featureActions.UpdateTransientUserStatusRequest({
				userId: action.value.userId,
				userStatus: action.value.userStatus,
				wasDelegating: status.isDelegating,
				wasDelegated: status.isDelegated,
			});

			// 1. This user was delegating
			// We use the /delegatingUpdate endpoint to revoke delegations from him to others
			if (status.isDelegating) {
				result.push(AppStoreActions.OpenConfirmationModal(
					{ textsKey: `Delegations.DelegatingUser.` },
					userStatusAction
				))

			// 2. This user was delegated
			// We use the /delegatedUpdate endpoint to revoke delegations from others to him
			} else if (status.isDelegated) {
				result.push(AppStoreActions.OpenConfirmationModal(
					{ textsKey: `Delegations.DelegatedUser.` },
					AppStoreActions.OpenChoicesModal(
						{ textsKey: `Delegations.UserDeactivation.` },
						null,
						{ label: 'InactivityPeriod.Title', do: [
							featureActions.OpenUserInactivityForm({ user, mode: ActionType.CREATE })
						] },
						{ label: 'Delegations.Choice', do: [
							featureActions.OpenUserDelegationForm({ user, currentUser, mode: ActionType.CREATE })
						]},
					)
				))

			// 3. This user was neither delegating nor delegated
			// switch to Inactive, we show the workflow modal
			} else if (action.value.userStatus === UserStatus.Inactive) {
				result.push(AppStoreActions.OpenChoicesModal(
					{ textsKey: `Delegations.UserDeactivation.` },
					null,
					{ label: 'InactivityPeriod.Title', do: [
						featureActions.OpenUserInactivityForm({ user, mode: ActionType.CREATE })
					] },
					{ label: 'Delegations.Choice', do: [
						featureActions.OpenUserDelegationForm({ user, currentUser, mode: ActionType.CREATE })
					]},
				))

			} else if (action.value.userStatus === UserStatus.Active && user.inactivityBeginDate) {
				result.push(featureActions.UpdateSubsidiaryUserInactivityFormRequest({
					mode: ActionType.UPDATE,
					forUserId: user.userId,
					reactivateUser: true,
					begin: null,
					end: null,
				}))

			// switch to Inactive, just dispatch to status action
			} else if (action.value.userStatus === UserStatus.Active) {
				result.push(userStatusAction)
			}

			return result
		})
	))
}
